import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAiSlidePanel } from "../contexts/ai-slide-panel-context";
import * as styles from "../styles/ai-slide-panel-layout.module.css";

interface AiSlidePanelLayoutProps {
  children: ReactNode;
  panel: ReactNode;
  panelWidth?: "sm" | "md" | "lg";
}

const MIN_PANEL_WIDTH = 324;
const MAX_PANEL_WIDTH = 548;

const DEFAULT_WIDTHS = { sm: 364, md: 548, lg: 548 };

export const AiSlidePanelLayout = memo(function AiSlidePanelLayout({
  children,
  panel,
  panelWidth = "sm",
}: AiSlidePanelLayoutProps) {
  const { isOpen, setPanelWidth: setContextPanelWidth } = useAiSlidePanel();
  const panelRef = useRef<HTMLDivElement>(null);
  const [currentPanelWidth, setCurrentPanelWidth] = useState<number>(
    DEFAULT_WIDTHS[panelWidth],
  );
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Sync initial panel width to context
  useEffect(() => {
    setContextPanelWidth(currentPanelWidth);
  }, [currentPanelWidth, setContextPanelWidth]);

  useEffect(() => {
    if (isOpen && panelRef.current) {
      setTimeout(() => {
        const input = panelRef.current?.querySelector("input, textarea");
        if (
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement
        ) {
          input.focus();
        }
      }, 300);
    }
  }, [isOpen]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = currentPanelWidth;
    },
    [currentPanelWidth],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.min(
        MAX_PANEL_WIDTH,
        Math.max(MIN_PANEL_WIDTH, resizeStartWidth.current + deltaX),
      );
      setCurrentPanelWidth(newWidth);
      setContextPanelWidth(newWidth);
    },
    [isResizing, setContextPanelWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div
      className={`${styles.layoutContainer} ${isOpen ? styles.layoutContainerOpen : ""} ${isResizing ? styles.layoutContainerResizing : ""}`}
      style={{
        gridTemplateColumns: isOpen
          ? `1fr 6px ${currentPanelWidth}px`
          : "1fr 0px 0px",
      }}
    >
      <div className={styles.mainContent}>{children}</div>

      {isOpen && (
        <div className={styles.resizeDivider} onMouseDown={handleMouseDown} />
      )}

      <div
        className={styles.slidePanel}
        ref={panelRef}
        {...(!isOpen && { inert: "" })}
      >
        {panel}
      </div>
    </div>
  );
});
